// Marco de panel de consola: title-bar con LED de disponibilidad + contador
// de items + timestamp del último fetch. El contenido degrada solo:
// - datos → children
// - { available: false } → LED rojo + código de error
// - undefined (sección no cargada aún) → placeholder

import type { ReactNode } from 'react';
import { isUnavailable } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { StatusLed } from './StatusLed';

export function SectionPanel({
  title,
  data,
  updatedAt,
  count,
  children,
  className = '',
}: {
  title: string;
  /** El valor crudo de la sección (para detectar unavailable/loading). */
  data: unknown;
  updatedAt?: number | undefined;
  count?: number | undefined;
  children: ReactNode;
  className?: string;
}) {
  const { t, locale } = useI18n();
  const unavailable = isUnavailable(data);
  const loading = data === undefined;

  return (
    <section
      aria-label={title}
      className={`flex flex-col rounded border border-surface-border bg-surface ${className}`}
    >
      <header className="flex items-center justify-between gap-2 border-b border-surface-border bg-surface-raised px-3 py-1.5">
        <h2 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-muted">
          <StatusLed tone={unavailable ? 'error' : loading ? 'idle' : 'success'} />
          {title}
          {count !== undefined && (
            <span className="rounded bg-surface-border px-1.5 text-[10px] tabular-nums text-ink">
              {count}
            </span>
          )}
        </h2>
        {updatedAt !== undefined && (
          <time className="text-[10px] tabular-nums text-ink-subtle" title={t('panel.lastFetch')}>
            {new Date(updatedAt).toLocaleTimeString(locale, { hour12: false })}
          </time>
        )}
      </header>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {unavailable ? (
          <p className="text-[11px] text-status-error">
            {t('panel.unavailable', { error: (data as { error: string }).error })}
          </p>
        ) : loading ? (
          <p className="text-[11px] text-ink-subtle">{t('panel.loading')}</p>
        ) : (
          children
        )}
      </div>
    </section>
  );
}

/** Mensaje estándar para secciones sin datos. */
export function EmptyHint({ children }: { children: ReactNode }) {
  return <p className="text-[11px] text-ink-subtle">{children}</p>;
}
